import { type ChangeEvent, type ReactNode, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, File, Text } from '@shared/widgets';
import PaperclipIcon from '@assets/icons/paperclip.svg';

interface FileInputProps {
  className?: string;
  label?: ReactNode;
  required?: boolean;
  value?: File;
  onChange?: (file: File) => void;
  onRemove?: () => void;
  onDownload?: () => void;
}

export const FileInput = (props: FileInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  const { t } = useTranslation();

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const uploadedFile = e.target.files[0];
    props.onChange?.(uploadedFile);
  };

  return (
    <div className={props.className}>
      {props.label && (
        <label htmlFor={id} className='w-full'>
          <Text size='sm' color='secondary'>
            {props.label} {props.required ? ' *' : ''}
          </Text>
        </label>
      )}
      {props.value ? (
        <File
          attachment={{ filename: props.value.name, fileSizeBytes: props.value.size }}
          downloadShow={!props.onDownload}
          onRemove={props.onRemove}
          onDownload={props.onDownload}
        />
      ) : (
        <>
          <input ref={inputRef} id={id} className='hidden' type='file' accept='image/*' onChange={changeHandler} />
          <label htmlFor={id}>
            <Button color='dashed' fullWidth onClick={() => inputRef.current?.click()}>
              <Icon icon={<PaperclipIcon />} color='inherit' />
              {t('buttons.addAttachment')}
            </Button>
          </label>
        </>
      )}
    </div>
  );
};
